import { useMemo } from "react"
import PropTypes from "prop-types"
import { capitalize, map, chain, keys, find, keyBy, get } from "lodash"
import classNames from "classnames"
import { Paper } from "@mui/material"
import { ROLE, PERMISSION_LEVEL } from "helpers/shapes"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS, hasPermission } from "helpers/permissions"
import { isAllAccessTeam } from "helpers/permissions/teams"
import { formatLongDate } from "helpers/formatters/dates"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { TEAM } from "./shapes"

export default function ResourceTeams({ account, resourceId, resourceType, permissionLevel, role, resourceTeams, accountTeams, update, tabs }) {
  const hasWriteAccess = role === "admin" || hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)

  const teamsById = useMemo(() => keyBy(accountTeams, "id"), [accountTeams])

  const availableOptionLabels = useMemo(() => chain(accountTeams)
    .filter(({ id }) => !find(resourceTeams, m => m.id === id))
    .keyBy("id")
    .mapValues(m => m.name)
    .value(),
  [accountTeams, resourceTeams])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        ${map(data.teams, (teamId, index) => `team${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { teamId: "${teamId}", permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceTeams",
      }, cb)
    }

    return {
      title: "Add Teams",
      submitText: "Add Team",
      track: {
        type: "resource-permissions-add-team",
        payload: ({ teams, permissionLevel }) => ({
          teams: map(teams, id => get(teamsById, [id, "name"], id)),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "teams",
        label: "Teams",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, resourceId, resourceType, update, account, teamsById])

  const editForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { teamId: "${data.id}", permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId,
        resourceType,
        permissionLevel: data.permissionLevel,
      }

      update(query, variables, account, {
        reloadData: "resourceTeams",
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "resource-permissions-edit-team",
        payload: ({ id, permissionLevel }) => ({
          resource: get(teamsById, [id, "name"], id),
          permissionLevel,
        }),
      },
      isEnabled: (team) => hasWriteAccess && !isAllAccessTeam(team.alias),
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [resourceId, resourceType, update, account, hasWriteAccess, teamsById])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!) {
        ${map(data, (team, index) => `team${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { teamId: "${team.id}", permissionLevel: NONE }) { id }`)}
      }`

      const variables = { resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceTeams",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "resource-permissions-remove-team",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: (team) => hasWriteAccess && !isAllAccessTeam(team.alias),
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, resourceId, resourceType, update])

  return <div className={classNames("modules_resource-teams", "with-header")}>
    <UpsellTabs id="resource-teams" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Resource Permissions"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={resourceTeams}
        columns={[{
          name: "Name",
          id: "name",
          linkTo: team => `/${account}/home/teams/${team.alias}`,
        }, {
          name: "Description",
          id: "description",
        }, {
          name: "Created",
          id: "createdAt",
          formatter: formatLongDate,
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

ResourceTeams.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  permissionLevel: PERMISSION_LEVEL,
  resourceId: PropTypes.string,
  resourceType: PropTypes.string,
  resourceTeams: PropTypes.arrayOf(TEAM).isRequired,
  accountTeams: PropTypes.arrayOf(TEAM).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
